// Auth state backed by sessionStorage. The Google ID token survives page
// refreshes (sessionStorage persists across reload) but is gone when the tab
// closes — same security posture as the in-memory store for XSS (any injected
// JS can read either), strictly better UX because operators no longer hit
// /sign-in on every reload. Trade-off discussed in ADMIN_UI §5.5 v0.7.
//
// Why not localStorage: persists across tab close → broader exfiltration
// window if a token leaks via dev tools / shared machine. Why not in-memory
// only: GIS silent re-auth is unreliable in practice (gsi/status 403s in
// some Chrome states even with origins configured), which means a page
// refresh repeatedly drops the user back to the sign-in screen.
//
// A silent-refresh hook (set by the sign-in page once GIS is initialised)
// is fired ~5 min before expiry; if it doesn't refresh in time, the user
// is signed out.

import { create } from 'zustand';
import { HOSTED_DOMAIN } from './config.js';
import { decodeIdToken } from './jwt.js';

export type AuthUser = { email: string; name?: string };

// Thrown by signIn when the verified ID token's `hd` claim is missing or
// doesn't match the configured hosted domain. The sign-in page catches this
// to show the wrong-domain toast (§5.1.5).
export class WrongDomainError extends Error {
  constructor(public hd: string | undefined) {
    super(`Domain "${hd ?? '(none)'}" is not allowed`);
    this.name = 'WrongDomainError';
  }
}

type State = {
  idToken: string | null;
  user: AuthUser | null;
  expiresAt: number | null; // ms epoch
  refreshFn: (() => void) | null;
  refreshTimer: ReturnType<typeof setTimeout> | null;
};

type Actions = {
  signIn: (token: string) => AuthUser;
  signOut: () => void;
  // The sign-in page wires GIS's `prompt('none')` here once GIS is loaded;
  // api-client.ts invokes it on 401 to trigger silent refresh. Test code can
  // pass an arbitrary callback.
  setRefreshFn: (fn: (() => void) | null) => void;
  isAuthenticated: () => boolean;
};

const REFRESH_LEAD_MS = 5 * 60 * 1000;
const STORAGE_KEY = 'er-admin-auth';

type StoredAuth = { idToken: string; user: AuthUser; expiresAt: number };

function readStorage(): StoredAuth | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredAuth>;
    if (
      typeof parsed.idToken !== 'string' ||
      typeof parsed.expiresAt !== 'number' ||
      !parsed.user ||
      typeof parsed.user.email !== 'string'
    ) {
      return null;
    }
    if (parsed.expiresAt <= Date.now()) {
      // Stale token — clear it so we don't try to use it.
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed as StoredAuth;
  } catch {
    return null;
  }
}

function writeStorage(payload: StoredAuth): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage can throw under quota / disabled. Fall back to
    // memory-only — the user will lose the token on refresh but the
    // current session keeps working.
  }
}

function clearStorage(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

export const useAuthStore = create<State & Actions>((set, get) => {
  // Rehydrate at module-load time. The refresh timer is scheduled here too so
  // a re-mount picks up the existing token AND continues the expiry countdown.
  const stored = readStorage();
  const initialTimer = stored
    ? setTimeout(
        () => {
          const fn = get().refreshFn;
          if (fn) fn();
          else get().signOut();
        },
        Math.max(stored.expiresAt - Date.now() - REFRESH_LEAD_MS, 0),
      )
    : null;

  return {
    idToken: stored?.idToken ?? null,
    user: stored?.user ?? null,
    expiresAt: stored?.expiresAt ?? null,
    refreshFn: null,
    refreshTimer: initialTimer,

    signIn: (token) => {
      const claims = decodeIdToken(token);
      if (claims.email_verified !== true) {
        throw new Error('Email not verified');
      }
      // Client-side hd check is a UX guard; the api re-verifies (§5.2). The
      // server returns 401 for absent hd (consumer account) and 403 for a
      // wrong-hd one; for the SPA toast we lump them together.
      if (!claims.hd || claims.hd !== HOSTED_DOMAIN) {
        throw new WrongDomainError(claims.hd);
      }

      const expiresAt = claims.exp * 1000;
      const user: AuthUser = {
        email: claims.email,
        ...(claims.name ? { name: claims.name } : {}),
      };

      const prev = get().refreshTimer;
      if (prev) clearTimeout(prev);
      const refreshIn = Math.max(expiresAt - Date.now() - REFRESH_LEAD_MS, 0);
      const refreshTimer = setTimeout(() => {
        const fn = get().refreshFn;
        if (fn) fn();
        else get().signOut();
      }, refreshIn);

      writeStorage({ idToken: token, user, expiresAt });
      set({ idToken: token, user, expiresAt, refreshTimer });
      return user;
    },

    signOut: () => {
      const prev = get().refreshTimer;
      if (prev) clearTimeout(prev);
      clearStorage();
      set({ idToken: null, user: null, expiresAt: null, refreshTimer: null });
    },

    setRefreshFn: (fn) => set({ refreshFn: fn }),

    isAuthenticated: () => {
      const { idToken, expiresAt } = get();
      if (!idToken || !expiresAt) return false;
      return expiresAt > Date.now();
    },
  };
});
